// Las variantes se arman por PROPIEDADES, como en Tienda Nube —
// ADR-2026-10-06-variantes-por-propiedades.
//
// Antes había que nombrar el atributo a mano, apretar "Agregar variante" una vez
// por combinación y escribir el valor en cada una: seis talles en tres colores eran
// dieciocho altas a mano. Ahora se eligen las propiedades (Talle: S, M, L · Color:
// Negro, Blanco) y las variantes SON la combinación: salen solas.
//
// Lo que guarda la base no cambia: `atributos` (los nombres, en orden) y una fila
// por variante con sus `valores`. Los valores de cada propiedad no se guardan
// aparte: se leen de las variantes, en el orden en que aparecen.
//
// Sin React y sin red, para poder testear el plan entero.
import type { VarianteDeLanzamiento } from "./lanzamientosApi";

/** Una propiedad como la edita la pantalla. */
export interface Propiedad {
  nombre: string;
  valores: ValorDePropiedad[];
}

export interface ValorDePropiedad {
  texto: string;
  /**
   * El valor que tenía antes de editarse (`null` = recién agregado). Es lo que deja
   * RENOMBRAR un talle sin perder lo que su variante ya tenía cargado (stock,
   * precio, fotos): se reconoce por lo que era, no por lo que dice ahora.
   */
  original: string | null;
}

function limpio(v: string | null | undefined): string {
  return (v ?? "").trim();
}

function clave(v: string): string {
  return v
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

/** Los valores de la propiedad `i`, sin repetir y en el orden de las variantes. */
export function valoresDePropiedad(variantes: readonly VarianteDeLanzamiento[], i: number): string[] {
  const vistos = new Set<string>();
  const salida: string[] = [];
  for (const v of [...variantes].sort((a, b) => a.orden - b.orden)) {
    const x = limpio(v.valores[i]);
    if (x === "" || vistos.has(clave(x))) continue;
    vistos.add(clave(x));
    salida.push(x);
  }
  return salida;
}

/** Las propiedades actuales del producto, listas para editar. */
export function propiedadesDe(producto: {
  atributos: readonly string[];
  variantes: readonly VarianteDeLanzamiento[];
}): Propiedad[] {
  return producto.atributos.map((nombre, i) => ({
    nombre,
    valores: valoresDePropiedad(producto.variantes, i).map((texto) => ({ texto, original: texto })),
  }));
}

/** Cuántas variantes salen de combinar estas propiedades. Sin propiedades, ninguna. */
export function cuantasVariantes(propiedades: readonly Propiedad[]): number {
  const conValores = propiedades.filter((p) => p.valores.some((v) => limpio(v.texto) !== ""));
  if (conValores.length === 0) return 0;
  return conValores.reduce((n, p) => n * p.valores.filter((v) => limpio(v.texto) !== "").length, 1);
}

/** Todas las combinaciones, la primera propiedad por fuera: S/Negro, S/Blanco, M/Negro… */
export function combinaciones(listas: readonly (readonly string[])[]): string[][] {
  return listas.reduce<string[][]>(
    (acc, lista) => acc.flatMap((prefijo) => lista.map((v) => [...prefijo, v])),
    [[]],
  );
}

export interface PlanDeVariantes {
  atributos: string[];
  /** Variantes que siguen, con sus valores nuevos (sólo las que cambian). */
  actualizar: Array<{ id: string; valores: string[] }>;
  /** Combinaciones nuevas, con la posición que ocupan en el orden final. */
  crear: Array<{ valores: string[]; orden: number }>;
  /** Variantes que ya no corresponden a ninguna combinación. */
  quitar: string[];
  /** El orden final: un id existente o el índice de una de `crear`. */
  orden: Array<{ id: string } | { nueva: number }>;
}

/**
 * Qué hay que hacer en la base para que las variantes sean exactamente la
 * combinación de estas propiedades, tocando lo mínimo.
 *
 * Una variante que ya existe se CONSERVA (con su stock, precio y fotos) si su
 * combinación sigue: aunque se haya renombrado un valor, cambiado el orden o
 * sumado una propiedad nueva (ahí toma el primer valor de la nueva). Sólo se
 * quitan las combinaciones que ya no existen, y sólo se crean las que faltan.
 */
export function planDeVariantes(
  antes: { atributos: readonly string[]; variantes: readonly VarianteDeLanzamiento[] },
  propiedades: readonly Propiedad[],
): PlanDeVariantes {
  // Las propiedades sin nombre o sin valores no definen nada: se descartan. Dos valores que terminan
  // diciendo lo mismo son UNO, pero con TODOS sus orígenes: al reintentar un renombre que se guardó a
  // medias ("Small" ya renombrado y "S" que quedó), renombrar "S" a "Small" de nuevo junta las dos y
  // no borra las variantes que todavía decían "S" (review #168, segunda vuelta).
  const props = propiedades
    .map((p) => {
      const valores: Array<{ texto: string; originales: string[] }> = [];
      for (const v of p.valores) {
        const texto = limpio(v.texto);
        if (texto === "") continue;
        const original = v.original === null ? null : limpio(v.original);
        const ya = valores.find((o) => clave(o.texto) === clave(texto));
        if (ya) {
          if (original !== null) ya.originales.push(original);
        } else {
          valores.push({ texto, originales: original !== null ? [original] : [] });
        }
      }
      return { nombre: limpio(p.nombre), valores };
    })
    .filter((p) => p.nombre !== "" && p.valores.length > 0);

  const atributos = props.map((p) => p.nombre);
  const variantes = [...antes.variantes].sort((a, b) => a.orden - b.orden);

  if (props.length === 0) {
    return { atributos: [], actualizar: [], crear: [], quitar: variantes.map((v) => v.id), orden: [] };
  }

  // De qué atributo viejo sale cada propiedad (por nombre). Una propiedad nueva no sale de ninguno.
  const indiceViejo = props.map((p) => antes.atributos.findIndex((a) => clave(a) === clave(p.nombre)));

  // La combinación nueva de cada variante existente, o null si un valor suyo ya no está.
  const tuplaNueva = (v: VarianteDeLanzamiento): string[] | null => {
    const tupla: string[] = [];
    for (let j = 0; j < props.length; j++) {
      const p = props[j]!;
      const k = indiceViejo[j]!;
      if (k === -1) {
        tupla.push(p.valores[0]!.texto);
        continue;
      }
      const viejo = limpio(v.valores[k]);
      // Por lo que ERA (un renombre conserva la variante) y, si no, por lo que dice: destildar "S" y
      // volver a tildarla, o escribirla de nuevo, es la misma "S" y no una variante nueva vacía
      // (review #168).
      const valor =
        p.valores.find((x) => x.originales.some((o) => clave(o) === clave(viejo))) ??
        p.valores.find((x) => clave(x.texto) === clave(viejo));
      if (!valor) return null;
      tupla.push(valor.texto);
    }
    return tupla;
  };

  const porClave = new Map<string, VarianteDeLanzamiento>();
  for (const v of variantes) {
    const t = tuplaNueva(v);
    if (!t) continue;
    const c = t.map(clave).join("\u0000");
    // Si dos variantes caen en la misma combinación (se quitó la propiedad que las
    // distinguía), sigue la primera: es la que el usuario ve arriba.
    if (!porClave.has(c)) porClave.set(c, v);
  }

  const actualizar: PlanDeVariantes["actualizar"] = [];
  const crear: PlanDeVariantes["crear"] = [];
  const orden: PlanDeVariantes["orden"] = [];
  const siguen = new Set<string>();

  combinaciones(props.map((p) => p.valores.map((v) => v.texto))).forEach((tupla, i) => {
    const existente = porClave.get(tupla.map(clave).join("\u0000"));
    if (existente) {
      siguen.add(existente.id);
      const iguales =
        existente.valores.length === tupla.length && existente.valores.every((x, j) => limpio(x) === tupla[j]);
      if (!iguales) actualizar.push({ id: existente.id, valores: tupla });
      orden.push({ id: existente.id });
    } else {
      orden.push({ nueva: crear.length });
      crear.push({ valores: tupla, orden: i });
    }
  });

  return { atributos, actualizar, crear, quitar: variantes.filter((v) => !siguen.has(v.id)).map((v) => v.id), orden };
}

// ── Valores sugeridos ───────────────────────────────────────────────────────

/** Talles por grupo, como los ofrece Tienda Nube (más calzado y jeans, que en Argentina son números). */
export const TALLES: ReadonlyArray<{ grupo: string; valores: readonly string[] }> = [
  { grupo: "Adultos", valores: ["XS", "S", "M", "L", "XL", "XXL"] },
  { grupo: "Niños", valores: ["2", "4", "6", "8", "10", "12", "14", "16"] },
  { grupo: "Jeans", valores: ["36", "38", "40", "42", "44", "46", "48", "50"] },
  { grupo: "Calzado", valores: ["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45"] },
];

export function esPropiedadTalle(nombre: string | null | undefined): boolean {
  return ["talle", "talles", "talla", "tallas", "size"].includes(clave(nombre ?? ""));
}

/**
 * Lo que se escribió en el campo de "agregar", partido en valores: "S, M, L" son
 * tres. Así se pega una lista entera de una vez en vez de cargarla de a uno.
 */
export function partirValores(texto: string): string[] {
  const vistos = new Set<string>();
  return texto
    .split(/[,;\n]/)
    .map((v) => v.trim())
    .filter((v) => {
      if (v === "" || vistos.has(clave(v))) return false;
      vistos.add(clave(v));
      return true;
    });
}

export function mismoValor(a: string, b: string): boolean {
  return clave(a) === clave(b);
}
